import { ChartDataOptionsBuilder } from './chart-data-options-builder';
import {
  createDefaultChartDataOptionsBuilder,
  DefaultChartDataOptionsBuilder
} from './default-chart-data-options-builder';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import { FavouriteDataOptions } from '../common/favourite-data-options';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { DrillDownHandler } from '../common/drill-down-builder';
import { StackingOptions } from '../../constants';
import { ChartLayoutConfig } from '../common/layout-config';
import { getNumberOfRows } from '../common/chart-rows';
import { SeriesTypeColumnMappings } from 'app/components/favourite-layout/favourite/chart-data-options/types';
import { YAxisOptions } from 'highcharts';
import { MIN_NO_OF_CRITERIAS } from 'app/area/reports/containers/chart/components/attributes/constants';

export class BarChartDataOptionsBuilder implements ChartDataOptionsBuilder {
  private dataAdapter: FavouriteDataAdapter;
  private columnMappings: SeriesTypeColumnMappings = {
    mainSeries: {
      sel1: 'x',
      sel2: 'series',
      sel3: 'splitting',
      aggregation: 'y'
    },
    rollingAverageSeries: {
      sel1: 'x',
      sel2: 'series',
      sel3: 'splitting',
      aggregationRollAvg: 'y'
    },
    targetSeries: {
      sel1: 'x',
      sel2: 'series',
      sel3: 'splitting',
      target: 'y'
    }
  };

  private chartDataOptionsBuilder: DefaultChartDataOptionsBuilder;
  private chartDataOptions: Highcharts.Options;

  constructor(dataOptions: FavouriteDataOptions, dataAdapter: FavouriteDataAdapter, drillDownHandler: DrillDownHandler) {
    this.dataAdapter = dataAdapter;
    this.chartDataOptionsBuilder = createDefaultChartDataOptionsBuilder(dataOptions, dataAdapter, drillDownHandler, this.columnMappings);
    this.chartDataOptionsBuilder.getAxisMin = (c, d) => this.getAxisMin(c, d);
    this.chartDataOptionsBuilder.getAxisMax = (c, d) => this.getAxisMax(c, d);
    this.chartDataOptionsBuilder.getExtraGraphHeight = this.getExtraGraphHeight;
    this.chartDataOptionsBuilder.isXAxisReversedByDefault = this.isXAxisReversedByDefault;
  }

  public setOptions(options: Highcharts.Options, splitRow: FavouriteRowValue, seriesValues: FavouriteRowValue[]) {
    this.chartDataOptions = options;
    this.setStackingOptions(options);
    this.chartDataOptionsBuilder.hideSeriesIfUnsupportedAggrType(options);
    this.chartDataOptionsBuilder.setOptions(options, splitRow, seriesValues);
    this.setAxisLabels(options);
  }

  private getStackingOptionValue() {
    const { bar: { stacking } = { stacking: undefined } } = this.chartDataOptions.plotOptions;
    return stacking;
  }

  public colIndexOf(typeName: string, seriesType: string) {
    return this.chartDataOptionsBuilder.colIndexOf(typeName, seriesType);
  }

  public isXAxisReversedByDefault() {
    return true;
  }

  private getAxisMin(colIndex: number, dataAdapter: FavouriteDataAdapter): number {
    const stackingOption = this.getStackingOptionValue();
    if (stackingOption === StackingOptions.PERCENT) {
      return 0;
    }

    return dataAdapter.columnExtremes[colIndex].min;
  }

  private getMaximumValueForStackedColumn(colIndex: number, dataAdapter: FavouriteDataAdapter): number {
    //Because of stacking.
    let max = 0;

    for (const category of dataAdapter.columnCategories[0]) {
      let categorySum = 0;

      for (const row of dataAdapter.dataModel.rows) {
        if (row[0].value === category.value) {
          categorySum += row[colIndex].value;
        }
      }

      if (max < categorySum) {
        max = categorySum;
      }
    }
    return max;
  }

  private getAxisMax(colIndex: number, dataAdapter: FavouriteDataAdapter): number {
    const stackingOption = this.getStackingOptionValue();

    if (stackingOption === StackingOptions.PERCENT) {
      return 100;
    } else if (stackingOption === StackingOptions.NORMAL) {
      return this.getMaximumValueForStackedColumn(colIndex, dataAdapter);
    }

    return dataAdapter.columnExtremes[colIndex].max;
  }

  private getExtraGraphHeight(options: Highcharts.Options, dataAdapter: FavouriteDataAdapter) {

    const config = dataAdapter.layoutConfig as ChartLayoutConfig;

    const rowHeight = config.chartStyle('barChartHeightGrowthPerBar');
    const allowedNumberOfRows = config.chartStyle('barChartHeightGrowIfMoreThan') || 0;
    const xColumnIndex = this.colIndexOf('x', 'mainSeries');

    return rowHeight
      ? Math.max(rowHeight * (getNumberOfRows(options, dataAdapter, xColumnIndex) - allowedNumberOfRows), 0)
      : 0;
  }

  private setAxisLabels(options: Highcharts.Options) {
    if (this.chartDataOptionsBuilder.isPercentEnabledForBarColumnAreaChart()) {
      (options.yAxis as YAxisOptions).labels.formatter = undefined;
      (options.yAxis as YAxisOptions).labels.format = '{value}%';
    }
  }

  private setStackingOptions(options: Highcharts.Options) {
    options.plotOptions.bar = options.plotOptions.bar || {};
    const isPercentEnabled = this.chartDataOptionsBuilder.isPercentEnabledForBarColumnAreaChart();
    const isStackedEnabled = this.chartDataOptionsBuilder.isStackedEnabledForBarColumnAreaChart();
    const isMinNoOfCriterias = this.dataAdapter.selectionCount >= MIN_NO_OF_CRITERIAS;

    if (isStackedEnabled && isMinNoOfCriterias) {
      this.dataAdapter.showPercentageInTooltip = true;
    }

    // Set stacking in plotOptions base on chart layout attribute
    if (isStackedEnabled && isPercentEnabled) {
      options.plotOptions.bar.stacking = StackingOptions.PERCENT;
    } else if (isStackedEnabled) {
      options.plotOptions.bar.stacking = StackingOptions.NORMAL;
    } else {
      options.plotOptions.bar.stacking = undefined;
    }
  }
}
